import { CloseOutlined, RightOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Form, Input, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useParams } from "react-router-dom";
import { OrganizationDto } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { CrudModals } from "../components/CrudModals";
import { CrudToolbar } from "../components/CrudToolbar";
import { EditorPanel } from "../components/EditorPanel";
import { EmptyState } from "../components/EmptyState";
import { PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { useColumnSearch } from "../components/useColumnSearch";
import {
    addOrganization,
    clearErrorOrganization,
    deleteOrganization,
    fetchOrganizations,
    selectOrganization,
    startAddOrganization,
    startDeleteOrganization,
    updateOrganization
} from "../features/organizationsSlice";

export const OrganizationsPage = () => {

    const dispatch = useAppDispatch()
    const { t } = useTranslation();
    const organizations = useAppSelector(state => state.organizations.organizations);
    const selectedOrganization = useAppSelector(state => state.organizations.selectedOrganization);
    const status = useAppSelector(state => state.organizations.status);
    const error = useAppSelector(state => state.organizations.error);
    const params = useParams();

    useEffect(() => {
        dispatch(fetchOrganizations());
    }, []);

    const columnSearch = useColumnSearch<OrganizationDto>({
        placeholder: t("placeholder.searchOrganization"),
        getSearchValues: organization => [organization.name]
    });

    if (params.organizationId !== undefined) {
        return <Outlet />
    }

    const onRow = (organization: OrganizationDto) => ({
        onClick: () => {
            dispatch(selectOrganization(organization))
        },
    });

    const columns: ColumnsType<OrganizationDto> = [
        {
            width: '100%',
            title: t("column.name"),
            dataIndex: 'name',
            key: 'name',
            ...columnSearch,
            sorter: (a, b) => a.name! > b.name! ? -1 : 1,
            ellipsis: true,
            render: (text, record) => (
                <div className="row-trailing">
                    <span className="text-overflow">{text}</span>
                    <Link
                        className="row-drill"
                        to={record.id!}
                        onClick={event => event.stopPropagation()}
                        aria-label={t("page.organization.title")}
                    >
                        <RightOutlined />
                    </Link>
                </div>
            )
        }
    ];

    const getRowClassName = (organization: OrganizationDto) => {
        return selectedOrganization != null && selectedOrganization.id === organization.id ? 'bg-selected' : '';
    }

    const changeOrganizationName = (name: string) => {
        dispatch(selectOrganization({ ...selectedOrganization, name }));
    }

    const onSaveOrganization = () => {
        if (selectedOrganization?.name == null) return;

        if (selectedOrganization.id == null) {
            dispatch(addOrganization(selectedOrganization.name));
            return;
        }

        dispatch(updateOrganization(selectedOrganization));
    }

    const canSave = selectedOrganization?.name != null && selectedOrganization.name.trim().length > 0;

    const addForm = status === "adding" ? (
        <EditorPanel
            title={t(selectedOrganization?.id != null ? "editor.edit" : "editor.new")}
            actions={
                <>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() => dispatch(clearErrorOrganization())}
                    >
                        {t("actions.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        disabled={!canSave}
                        icon={<SaveOutlined />}
                        onClick={onSaveOrganization}
                    >
                        {t("actions.save")}
                    </Button>
                </>
            }
        >
            <Form name="basic" layout="vertical" autoComplete="off">
                <Form.Item label={t("field.organizationName")} style={{ maxWidth: 460 }}>
                    <Input
                        placeholder={t("placeholder.organizationName")}
                        value={selectedOrganization?.name ?? ""}
                        onChange={e => changeOrganizationName(e.target.value)}
                        onPressEnter={() => { if (canSave) onSaveOrganization(); }}
                    />
                </Form.Item>
            </Form>
        </EditorPanel>
    ) : null;

    const content = status === 'loading' || status === 'idle'
        ? <TableSkeleton />
        : <>
            {status !== "adding" ? <CrudToolbar
                hasSelection={selectedOrganization != null}
                canEdit={selectedOrganization?.id != null}
                selectionLabel={selectedOrganization?.name}
                onDelete={() => { if (selectedOrganization != null) dispatch(startDeleteOrganization()); }}
                onEdit={() => { if (selectedOrganization != null) dispatch(startAddOrganization(selectedOrganization)); }}
                onAdd={() => dispatch(startAddOrganization({} as OrganizationDto))}
                onReload={() => dispatch(fetchOrganizations())}
                onClearSelection={() => dispatch(selectOrganization(undefined))}
            /> : null}
            {addForm}
            <div className="card card--flush">
                <Table
                    rowKey={(organization: OrganizationDto) => organization.id!}
                    rowClassName={getRowClassName}
                    onRow={onRow}
                    dataSource={organizations}
                    columns={columns}
                    locale={{ emptyText: <EmptyState /> }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                />
            </div>

            <CrudModals
                status={status}
                error={error}
                entityKey="organization"
                deleteTarget={selectedOrganization?.name}
                onDismiss={() => dispatch(clearErrorOrganization())}
                onConfirmDelete={() => { if (selectedOrganization != null) dispatch(deleteOrganization(selectedOrganization.id!)); }}
            />
        </>

    return (
        <>
            <PageHeader
                title={t("page.organizations.title")}
                count={organizations?.length}
            />
            {content}
        </>
    );
}
